import * as THREE from 'three';
import { orientFaceOutline } from '../building/FaceHighlight';

export type FaceUiArrow = 'up' | 'right' | 'down' | 'left';
export type FaceUiVisibility = 'focused' | 'permanent';
export type FaceUiPoint = [number, number];
export type FaceUiFace =
  | 'top'
  | 'bottom'
  | 'north'
  | 'south'
  | 'east'
  | 'west';
export type FaceUiFaceFilter =
  | 'any'
  | 'vertical'
  | 'horizontal'
  | FaceUiFace[];

export interface FaceUiIconStyle {
  fill: string;
  stroke: string;
  strokeWidth: number;
}

export interface FaceUiIconAsset {
  src: string;
  rotation?: number;
  fallback?: FaceUiArrow;
}

export interface FaceUiButton {
  id: string;
  polygon: FaceUiPoint[];
  icon?: FaceUiArrow | FaceUiIconAsset;
  text?: string;
  tooltip?: string;
  content?: FaceUiContentLayout;
}

export interface FaceUiContentLayout {
  direction?: 'row' | 'column';
  align?: 'start' | 'center' | 'end';
  /** Font size as a fraction of the face canvas size (default 0.045). */
  fontScale?: number;
}

export interface FaceUiColorScheme {
  background?: string;
  border?: string;
  buttonBackground?: string;
  buttonHoverBackground?: string;
  buttonBorder?: string;
  text?: string;
  textHover?: string;
  iconFill?: string;
  iconHoverFill?: string;
  iconStroke?: string;
  iconHoverStroke?: string;
  iconStrokeWidth?: number;
  iconHoverStrokeWidth?: number;
  tooltipBackground?: string;
  tooltipText?: string;
  tooltipBorder?: string;
}

export interface FaceUiLayout {
  id: string;
  hotkey?: string;
  momentary?: boolean;
  buttons: FaceUiButton[];
  colors?: FaceUiColorScheme;
  content?: FaceUiContentLayout;
}

export interface FaceUiDefinition {
  id: string;
  defaultLayout: string;
  layouts: FaceUiLayout[];
  colors?: FaceUiColorScheme;
  faces?: FaceUiFaceFilter;
}

export interface FaceUiTarget {
  x: number;
  y: number;
  z: number;
  nx: number;
  ny: number;
  nz: number;
}

export interface FaceUiModifiers {
  control: boolean;
  alt: boolean;
  shift: boolean;
}

export type FaceUiAxisVector = [number, number, number];
export interface FaceUiAxes {
  right: FaceUiAxisVector;
  up: FaceUiAxisVector;
}

export interface FaceUiBinding {
  definitionId: string;
  visibility: FaceUiVisibility;
  matches(blockType: number): boolean;
  onActivate(
    buttonId: string,
    layoutId: string,
    modifiers: FaceUiModifiers,
    target: FaceUiTarget,
    axes: FaceUiAxes,
  ): void;
}

/** Registry and renderer for reusable interactive panels attached to block faces. */
export class FaceUiSystem {
  private readonly definitions = new Map<string, FaceUiDefinition>();
  private readonly bindings: FaceUiBinding[] = [];
  private readonly permanent = new Map<string, FaceUiPanel>();
  private focused: FaceUiPanel | null = null;
  private focusedBinding: FaceUiBinding | null = null;
  private readonly tooltip = document.createElement('div');
  private cursorX = window.innerWidth / 2;
  private cursorY = window.innerHeight / 2;

  constructor(private readonly scene: THREE.Scene) {
    this.tooltip.setAttribute('role', 'tooltip');
    this.tooltip.style.cssText = [
      'position:fixed',
      'display:none',
      'pointer-events:none',
      'z-index:10000',
      'padding:7px 11px',
      'border:1px solid',
      'border-radius:4px',
      'font:600 13px sans-serif',
      'white-space:nowrap',
      'transform:translate(-50%,-100%)',
      'user-select:none',
      '-webkit-user-select:none',
    ].join(';');
    document.body.append(this.tooltip);
    document.addEventListener('pointermove', (event) => {
      if (document.pointerLockElement) return;
      this.cursorX = event.clientX;
      this.cursorY = event.clientY;
    });
    document.addEventListener('keydown', (event) => {
      if (
        event.repeat ||
        !this.focused?.visible ||
        isEditableElement(event.target) ||
        isInventoryOpen()
      ) return;
      if (this.focused.switchLayoutByHotkey(event.code, event.key)) {
        event.preventDefault();
      }
    });
    document.addEventListener('keyup', (event) => {
      if (
        !this.focused?.visible ||
        isEditableElement(event.target) ||
        isInventoryOpen()
      ) return;
      if (this.focused.releaseMomentaryLayout(event.code, event.key)) {
        event.preventDefault();
      }
    });
  }

  get hasFocusedUi(): boolean {
    return this.focused?.visible ?? false;
  }

  registerDefinition(definition: FaceUiDefinition): void {
    this.definitions.set(definition.id, definition);
  }

  registerBinding(binding: FaceUiBinding): void {
    if (!this.definitions.has(binding.definitionId)) {
      throw new Error(`Unknown face UI definition: ${binding.definitionId}`);
    }
    this.bindings.push(binding);
  }

  updateFocused(
    camera: THREE.PerspectiveCamera,
    target: FaceUiTarget | null,
    blockType: number,
  ): void {
    const binding = target
      ? this.bindings.find(
        (candidate) =>
          candidate.visibility === 'focused' &&
          candidate.matches(blockType) &&
          faceAllowed(
            this.definitions.get(candidate.definitionId)?.faces,
            target,
          ),
      ) ?? null
      : null;
    if (!target || !binding) {
      this.focused?.hide();
      this.focusedBinding = null;
      this.hideTooltip();
      return;
    }
    if (
      !this.focused ||
      this.focused.definition.id !== binding.definitionId
    ) {
      this.focused?.dispose();
      this.focused = this.createPanel(binding.definitionId);
    }
    this.focusedBinding = binding;
    this.focused.show(target);
    const pick = this.focused.pick(camera);
    this.focused.setHovered(pick?.buttonId ?? null);
    this.updateTooltip();
  }

  handleClick(
    camera: THREE.PerspectiveCamera,
    button: number,
    modifiers: FaceUiModifiers,
  ): boolean {
    if (button !== 0 || !this.focused?.visible || !this.focusedBinding) return false;
    const pick = this.focused.pick(camera);
    const buttonId = pick?.buttonId;
    const target = this.focused.target;
    if (!buttonId || !target) return false;
    this.focusedBinding.onActivate(
      buttonId,
      this.focused.layout.id,
      modifiers,
      target,
      this.focused.faceAxes(),
    );
    return true;
  }

  /** Attach a panel that remains visible until explicitly removed. */
  attachPermanent(
    key: string,
    definitionId: string,
    target: FaceUiTarget,
  ): void {
    let panel = this.permanent.get(key);
    if (!panel || panel.definition.id !== definitionId) {
      panel?.dispose();
      panel = this.createPanel(definitionId);
      this.permanent.set(key, panel);
    }
    panel.show(target);
    panel.setHovered(null);
  }

  removePermanent(key: string): void {
    this.permanent.get(key)?.dispose();
    this.permanent.delete(key);
  }

  private createPanel(definitionId: string): FaceUiPanel {
    const definition = this.definitions.get(definitionId);
    if (!definition) throw new Error(`Unknown face UI definition: ${definitionId}`);
    const panel = new FaceUiPanel(definition);
    this.scene.add(panel.mesh);
    return panel;
  }

  private updateTooltip(): void {
    const text = this.focused?.hoveredTooltip;
    if (!text) {
      this.hideTooltip();
      return;
    }
    const colors = this.focused!.colors;
    this.tooltip.textContent = text;
    this.tooltip.style.display = 'block';
    this.tooltip.style.background = colors.tooltipBackground;
    this.tooltip.style.color = colors.tooltipText;
    this.tooltip.style.borderColor = colors.tooltipBorder;
    const cursorX = document.pointerLockElement
      ? window.innerWidth / 2
      : this.cursorX;
    const cursorY = document.pointerLockElement
      ? window.innerHeight / 2
      : this.cursorY;
    const bounds = this.tooltip.getBoundingClientRect();
    const x = Math.max(
      bounds.width / 2 + 8,
      Math.min(window.innerWidth - bounds.width / 2 - 8, cursorX),
    );
    const y = Math.max(bounds.height + 16, cursorY - 14);
    this.tooltip.style.left = `${x}px`;
    this.tooltip.style.top = `${y}px`;
  }

  private hideTooltip(): void {
    this.tooltip.style.display = 'none';
  }
}

class FaceUiPanel {
  readonly mesh: THREE.Mesh<THREE.PlaneGeometry, THREE.MeshBasicMaterial>;
  visible = false;
  private readonly canvas = document.createElement('canvas');
  private readonly texture: THREE.CanvasTexture;
  private readonly raycaster = new THREE.Raycaster();
  private readonly screenCenter = new THREE.Vector2(0, 0);
  private readonly right = new THREE.Vector3();
  private readonly up = new THREE.Vector3();
  private hovered: string | null = null;
  private activeLayoutId: string;
  target: FaceUiTarget | null = null;

  constructor(readonly definition: FaceUiDefinition) {
    this.activeLayoutId = definition.defaultLayout;
    if (!definition.layouts.some((layout) => layout.id === this.activeLayoutId)) {
      throw new Error(`Unknown default Face UI layout: ${this.activeLayoutId}`);
    }
    this.canvas.width = this.canvas.height = 512;
    this.texture = new THREE.CanvasTexture(this.canvas);
    this.texture.colorSpace = THREE.SRGBColorSpace;
    this.mesh = new THREE.Mesh(
      new THREE.PlaneGeometry(1, 1),
      new THREE.MeshBasicMaterial({
        map: this.texture,
        transparent: true,
        side: THREE.DoubleSide,
        depthWrite: false,
        polygonOffset: true,
        polygonOffsetFactor: -2,
      }),
    );
    this.mesh.renderOrder = 15;
    this.mesh.visible = false;
    this.preloadLayoutIcons();
    this.draw();
  }

  get layout(): FaceUiLayout {
    return this.definition.layouts.find(
      (layout) => layout.id === this.activeLayoutId,
    )!;
  }

  get colors(): Required<FaceUiColorScheme> {
    return resolvedColors(this.definition.colors, this.layout.colors);
  }

  get hoveredTooltip(): string | undefined {
    return this.layout.buttons.find(
      (button) => button.id === this.hovered,
    )?.tooltip;
  }

  show(target: FaceUiTarget): void {
    this.target = { ...target };
    this.mesh.position.set(
      target.x + 0.5 + target.nx * 0.506,
      target.y + 0.5 + target.ny * 0.506,
      target.z + 0.5 + target.nz * 0.506,
    );
    orientFaceOutline(this.mesh, target.nx, target.ny, target.nz);
    this.mesh.updateMatrixWorld();
    this.mesh.visible = true;
    this.visible = true;
  }

  hide(): void {
    this.mesh.visible = false;
    this.visible = false;
    this.target = null;
    this.setHovered(null);
  }

  pick(camera: THREE.PerspectiveCamera): { buttonId: string } | null {
    this.raycaster.setFromCamera(this.screenCenter, camera);
    const intersection = this.raycaster.intersectObject(this.mesh, false)[0];
    if (!intersection?.uv) return null;
    const point: FaceUiPoint = [intersection.uv.x, 1 - intersection.uv.y];
    const buttonId = this.layout.buttons.find((button) =>
      pointInPolygon(point, button.polygon))?.id;
    return buttonId ? { buttonId } : null;
  }

  setHovered(buttonId: string | null): void {
    if (buttonId === this.hovered) return;
    this.hovered = buttonId;
    this.draw();
  }

  switchLayoutByHotkey(code: string, key = ''): boolean {
    const layout = this.definition.layouts.find(
      (candidate) => hotkeyMatches(candidate.hotkey, code, key),
    );
    if (!layout) return false;
    if (layout.id !== this.activeLayoutId) {
      this.activeLayoutId = layout.id;
      this.hovered = null;
      this.preloadLayoutIcons();
      this.draw();
    }
    return true;
  }

  releaseMomentaryLayout(code: string, key = ''): boolean {
    if (
      !this.layout.momentary ||
      !hotkeyMatches(this.layout.hotkey, code, key)
    ) return false;
    this.activeLayoutId = this.definition.defaultLayout;
    this.hovered = null;
    this.preloadLayoutIcons();
    this.draw();
    return true;
  }

  faceAxes(): FaceUiAxes {
    this.right.set(1, 0, 0).applyQuaternion(this.mesh.quaternion).round();
    this.up.set(0, 1, 0).applyQuaternion(this.mesh.quaternion).round();
    return {
      right: [this.right.x, this.right.y, this.right.z],
      up: [this.up.x, this.up.y, this.up.z],
    };
  }

  dispose(): void {
    this.mesh.removeFromParent();
    this.mesh.geometry.dispose();
    this.mesh.material.dispose();
    this.texture.dispose();
  }

  private draw(): void {
    const ctx = this.canvas.getContext('2d')!;
    const size = this.canvas.width;
    const colors = this.colors;
    ctx.clearRect(0, 0, size, size);
    ctx.fillStyle = colors.background;
    ctx.fillRect(0, 0, size, size);

    for (const button of this.layout.buttons) {
      const focused = button.id === this.hovered;
      drawPolygon(ctx, button.polygon, size);
      ctx.fillStyle = focused
        ? colors.buttonHoverBackground
        : colors.buttonBackground;
      ctx.fill();
      ctx.strokeStyle = colors.buttonBorder;
      ctx.lineWidth = 5;
      ctx.stroke();
      const content = { ...this.layout.content, ...button.content };
      const placement = contentPlacement(
        button.polygon,
        size,
        !!button.icon,
        !!button.text,
        content,
      );
      if (button.icon) {
        if (typeof button.icon === 'object') {
          const asset = SVG_ASSETS.get(button.icon.src);
          if (asset) {
            drawSvgIcon(
              ctx,
              asset,
              button.icon.rotation ?? 0,
              {
                fill: focused ? colors.iconHoverFill : colors.iconFill,
                stroke: focused ? colors.iconHoverStroke : colors.iconStroke,
                strokeWidth: focused
                  ? colors.iconHoverStrokeWidth
                  : colors.iconStrokeWidth,
              },
              placement.iconCenter,
              placement.iconSize,
            );
          } else if (button.icon.fallback) {
            drawArrow(
              ctx,
              button.icon.fallback,
              focused ? colors.iconHoverFill : colors.iconFill,
              focused ? colors.iconHoverStroke : colors.iconStroke,
              placement.iconCenter,
              placement.iconSize,
            );
          }
        } else {
          drawArrow(
            ctx,
            button.icon,
            focused ? colors.iconHoverFill : colors.iconFill,
            focused ? colors.iconHoverStroke : colors.iconStroke,
            placement.iconCenter,
            placement.iconSize,
          );
        }
      }
      if (button.text) {
        drawButtonText(
          ctx,
          button.text,
          size,
          focused ? colors.textHover : colors.text,
          placement.textPoint,
          placement.textAlign,
          content.fontScale,
        );
      }
    }
    ctx.strokeStyle = colors.border;
    ctx.lineWidth = 9;
    ctx.strokeRect(4.5, 4.5, size - 9, size - 9);
    this.texture.needsUpdate = true;
  }

  private preloadIcon(source: string): void {
    void loadSvgAsset(source).then(
      () => this.draw(),
      () => this.draw(),
    );
  }

  private preloadLayoutIcons(): void {
    for (const button of this.layout.buttons) {
      if (typeof button.icon === 'object') this.preloadIcon(button.icon.src);
    }
  }
}

interface SvgAsset {
  paths: Path2D[];
  viewBox: [number, number, number, number];
}

const SVG_ASSETS = new Map<string, SvgAsset>();
const SVG_LOADS = new Map<string, Promise<SvgAsset>>();
const DEFAULT_COLORS: Required<FaceUiColorScheme> = {
  background: 'rgba(248,250,246,0.18)',
  border: '#383e43',
  buttonBackground: 'rgba(255,255,255,0.025)',
  buttonHoverBackground: 'rgba(255,255,255,0.42)',
  buttonBorder: '#383e43',
  text: '#383e43',
  textHover: '#f28c28',
  iconFill: '#383e43',
  iconHoverFill: '#f28c28',
  iconStroke: '#ffffff',
  iconHoverStroke: '#ffffff',
  iconStrokeWidth: 10,
  iconHoverStrokeWidth: 10,
  tooltipBackground: 'rgba(30,32,34,0.94)',
  tooltipText: '#ffffff',
  tooltipBorder: '#ffffff',
};

function resolvedColors(
  definition?: FaceUiColorScheme,
  layout?: FaceUiColorScheme,
): Required<FaceUiColorScheme> {
  return { ...DEFAULT_COLORS, ...definition, ...layout };
}

function loadSvgAsset(source: string): Promise<SvgAsset> {
  const loaded = SVG_ASSETS.get(source);
  if (loaded) return Promise.resolve(loaded);
  let loading = SVG_LOADS.get(source);
  if (!loading) {
    loading = fetch(source)
      .then((response) => {
        if (!response.ok) throw new Error(`Could not load face UI icon: ${source}`);
        return response.text();
      })
      .then((markup) => {
        const document = new DOMParser().parseFromString(markup, 'image/svg+xml');
        const svg = document.querySelector('svg');
        const viewBox = svg?.getAttribute('viewBox')?.trim().split(/\s+/).map(Number);
        const paths = [...document.querySelectorAll('path')]
          .map((path) => path.getAttribute('d'))
          .filter((path): path is string => !!path)
          .map((path) => new Path2D(path));
        if (
          !viewBox ||
          viewBox.length !== 4 ||
          viewBox.some((value) => !Number.isFinite(value)) ||
          paths.length === 0
        ) {
          throw new Error(`Face UI icon needs a viewBox and at least one path: ${source}`);
        }
        const asset: SvgAsset = {
          paths,
          viewBox: viewBox as [number, number, number, number],
        };
        SVG_ASSETS.set(source, asset);
        return asset;
      });
    SVG_LOADS.set(source, loading);
  }
  return loading;
}

function drawSvgIcon(
  ctx: CanvasRenderingContext2D,
  asset: SvgAsset,
  rotation: number,
  style: FaceUiIconStyle,
  center: FaceUiPoint,
  iconSize: number,
): void {
  const [minX, minY, width, height] = asset.viewBox;
  const scale = iconSize / Math.max(width, height);
  ctx.save();
  ctx.translate(center[0], center[1]);
  ctx.rotate(rotation * Math.PI / 180);
  ctx.scale(scale, scale);
  ctx.translate(-minX - width / 2, -minY - height / 2);
  ctx.fillStyle = style.fill;
  ctx.strokeStyle = style.stroke;
  ctx.lineWidth = style.strokeWidth;
  ctx.lineJoin = 'round';
  for (const path of asset.paths) {
    ctx.fill(path);
    if (style.strokeWidth > 0) ctx.stroke(path);
  }
  ctx.restore();
}

function drawPolygon(
  ctx: CanvasRenderingContext2D,
  polygon: FaceUiPoint[],
  size: number,
): void {
  ctx.beginPath();
  ctx.moveTo(polygon[0][0] * size, polygon[0][1] * size);
  for (let index = 1; index < polygon.length; index++) {
    ctx.lineTo(polygon[index][0] * size, polygon[index][1] * size);
  }
  ctx.closePath();
}

function drawArrow(
  ctx: CanvasRenderingContext2D,
  direction: FaceUiArrow,
  color: string,
  outline: string,
  center: FaceUiPoint,
  iconSize: number,
): void {
  const vectors: Record<FaceUiArrow, FaceUiPoint> = {
    up: [0, -1],
    right: [1, 0],
    down: [0, 1],
    left: [-1, 0],
  };
  const [dx, dy] = vectors[direction];
  const length = iconSize * 0.5;
  const startX = center[0] - dx * length * 0.55;
  const startY = center[1] - dy * length * 0.55;
  const endX = center[0] + dx * length * 0.55;
  const endY = center[1] + dy * length * 0.55;
  const traceArrow = () => {
    ctx.beginPath();
    ctx.moveTo(startX, startY);
    ctx.lineTo(endX, endY);
    ctx.lineTo(
      endX - dx * iconSize * 0.16 - dy * iconSize * 0.13,
      endY - dy * iconSize * 0.16 + dx * iconSize * 0.13,
    );
    ctx.moveTo(endX, endY);
    ctx.lineTo(
      endX - dx * iconSize * 0.16 + dy * iconSize * 0.13,
      endY - dy * iconSize * 0.16 - dx * iconSize * 0.13,
    );
  };
  ctx.lineJoin = 'miter';
  ctx.lineCap = 'square';
  traceArrow();
  ctx.strokeStyle = outline;
  ctx.lineWidth = 18;
  ctx.stroke();
  traceArrow();
  ctx.strokeStyle = color;
  ctx.lineWidth = 10;
  ctx.stroke();
}

function drawButtonText(
  ctx: CanvasRenderingContext2D,
  text: string,
  size: number,
  color: string,
  point: FaceUiPoint,
  align: CanvasTextAlign,
  fontScale = 0.045,
): void {
  ctx.save();
  ctx.fillStyle = color;
  ctx.font = `700 ${Math.round(size * fontScale)}px sans-serif`;
  ctx.textAlign = align;
  ctx.textBaseline = 'middle';
  ctx.fillText(text, point[0], point[1]);
  ctx.restore();
}

interface ContentPlacement {
  iconCenter: FaceUiPoint;
  iconSize: number;
  textPoint: FaceUiPoint;
  textAlign: CanvasTextAlign;
}

function contentPlacement(
  polygon: FaceUiPoint[],
  size: number,
  hasIcon: boolean,
  hasText: boolean,
  layout: FaceUiContentLayout,
): ContentPlacement {
  const center = polygonCenter(polygon, size);
  const xs = polygon.map((point) => point[0] * size);
  const ys = polygon.map((point) => point[1] * size);
  const left = Math.min(...xs);
  const right = Math.max(...xs);
  const bottom = Math.max(...ys);
  const width = right - left;
  const height = bottom - Math.min(...ys);
  if (hasIcon && hasText && layout.direction === 'row') {
    const iconSize = Math.min(height * 0.46, size * 0.14);
    const padding = Math.max(size * 0.025, width * 0.07);
    const align = layout.align ?? 'start';
    if (align === 'end') {
      return {
        iconCenter: [right - padding - iconSize / 2, center[1]],
        iconSize,
        textPoint: [right - padding - iconSize - size * 0.025, center[1]],
        textAlign: 'right',
      };
    }
    if (align === 'center') {
      return {
        iconCenter: [center[0] - width * 0.14, center[1]],
        iconSize,
        textPoint: [center[0] - width * 0.03, center[1]],
        textAlign: 'left',
      };
    }
    return {
      iconCenter: [left + padding + iconSize / 2, center[1]],
      iconSize,
      textPoint: [left + padding + iconSize + size * 0.025, center[1]],
      textAlign: 'left',
    };
  }
  const iconSize = size * (hasText ? 0.16 : 0.23);
  const align = layout.align ?? 'center';
  const padding = Math.max(size * 0.025, width * 0.07);
  if (!hasIcon && hasText) {
    if (align === 'start') {
      return {
        iconCenter: center,
        iconSize,
        textPoint: [left + padding, center[1]],
        textAlign: 'left',
      };
    }
    if (align === 'end') {
      return {
        iconCenter: center,
        iconSize,
        textPoint: [right - padding, center[1]],
        textAlign: 'right',
      };
    }
  }
  return {
    iconCenter: [center[0], center[1] - (hasIcon && hasText ? size * 0.035 : 0)],
    iconSize,
    textPoint: [center[0], center[1] + (hasIcon ? size * 0.075 : 0)],
    textAlign: 'center',
  };
}

function polygonCenter(polygon: FaceUiPoint[], size: number): FaceUiPoint {
  return polygon.reduce(
    (sum, point) => [sum[0] + point[0], sum[1] + point[1]] as FaceUiPoint,
    [0, 0] as FaceUiPoint,
  ).map((value) => value / polygon.length * size) as FaceUiPoint;
}

function isEditableElement(target: EventTarget | null): boolean {
  return target instanceof HTMLElement &&
    (target.isContentEditable ||
      target instanceof HTMLInputElement ||
      target instanceof HTMLTextAreaElement ||
      target instanceof HTMLSelectElement);
}

function isInventoryOpen(): boolean {
  return !!document.getElementById('inventory-popup')?.classList.contains('visible')
    || !!document.getElementById('dev-popup')?.classList.contains('visible');
}

function hotkeyMatches(
  configured: string | undefined,
  code: string,
  key: string,
): boolean {
  return !!configured && (configured === code || configured === key);
}

function faceAllowed(
  filter: FaceUiFaceFilter | undefined,
  target: FaceUiTarget,
): boolean {
  if (!filter || filter === 'any') return true;
  if (filter === 'horizontal') return target.ny === 0;
  if (filter === 'vertical') return target.ny !== 0;
  return filter.includes(faceName(target));
}

function faceName(target: FaceUiTarget): FaceUiFace {
  if (target.ny > 0) return 'top';
  if (target.ny < 0) return 'bottom';
  if (target.nx > 0) return 'east';
  if (target.nx < 0) return 'west';
  return target.nz > 0 ? 'south' : 'north';
}

function pointInPolygon(point: FaceUiPoint, polygon: FaceUiPoint[]): boolean {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [xi, yi] = polygon[i];
    const [xj, yj] = polygon[j];
    const intersects = (yi > point[1]) !== (yj > point[1]) &&
      point[0] < ((xj - xi) * (point[1] - yi)) / (yj - yi) + xi;
    if (intersects) inside = !inside;
  }
  return inside;
}
